import React, { useState } from 'react';

import { GeneralActionInner } from '~/components/AiChat/SideBarDialog/Actions/Action/GeneralActionInner';
import type { AiAction } from '~/components/AiChat/types';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import ChevronDownIcon from '~/components/icons/ChevronDownIcon';
import colors from '~/theme/tailwind/colors';
import cn from '~/Utils/cn';

import ActionStateChip from '../../ActionStateChip';
import { ACTION_STATE_APPEARANCE } from '../../constants';
import PayloadList from '../../PayloadList';
import type { ActionState } from '../../types';
import { actionTitle } from '../../utils';
import { ACTION_HISTORY_TEXTS } from '../texts';
import type { ActionVersion } from '../utils';
import { toHistoryRows } from '../utils';

/** The text buttons the dialog pages and retries with, as the insight history draws its own. */
const TEXT_BUTTON_CLASSES = 'p-0 font-medium normal-case text-teal-700 hover:bg-transparent';

interface TimelineRowProps {
  /** The rail node's colour. */
  node: string;
  time?: string;
  label: string;
  /** After the label: the version's title, or the note the action was settled with. */
  detail?: string;
  isLast?: boolean;
  /** Opens and closes the row's body, where it has one. */
  onToggle?: () => void;
  isOpen?: boolean;
  children?: React.ReactNode;
}

/** One row of the history, on the rail that joins it to the next. */
const TimelineRow: React.FC<TimelineRowProps> = ({
  node,
  time,
  label,
  detail,
  isLast = false,
  onToggle,
  isOpen = false,
  children,
}) => {
  const head = (
    <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-8">
      {time ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {time}
        </Text>
      ) : null}
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {label}
      </Text>
      {detail ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {detail}
        </Text>
      ) : null}
    </span>
  );

  return (
    <li className="relative pl-24">
      {!isLast ? <span className="absolute bottom-0 left-[5px] top-16 w-[1px] bg-slate-200" aria-hidden /> : null}
      <span className={cn('absolute left-0 top-[11px] h-[11px] w-[11px] rounded-full', node)} aria-hidden />
      <div className="py-8">
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className="flex w-full cursor-pointer items-baseline gap-8 border-0 bg-transparent p-0 text-left"
          >
            {head}
            <span className={cn('shrink-0 transition-transform', { 'rotate-180': isOpen })}>
              <ChevronDownIcon size={14} iconColor={colors.grey[700]} />
            </span>
          </button>
        ) : (
          <div className="flex items-baseline gap-8">{head}</div>
        )}
        {isOpen && children ? <div className="mt-8 flex flex-col gap-8">{children}</div> : null}
      </div>
    </li>
  );
};

interface ActionHistoryDialogProps {
  action: AiAction;
  state: ActionState;
  /** The exposure, where there is one, as the card names it. */
  exposureLabel?: string;
  /** Every version read so far, newest first, as the route pages them. */
  versions: ActionVersion[];
  /** Whether older versions are still to be read. */
  hasMore: boolean;
  isLoading: boolean;
  isError: boolean;
  onLoadOlder: () => void;
  onRetry: () => void;
  onClose: () => void;
}

/**
 * How an action got to what it says now: every version of the recommendation, oldest first, then the
 * state it is in. A record and not a control surface — the card decides, the dialog explains — so the
 * last row offers nothing to act on.
 *
 * A row opens to the recommendation as it stood at that version: its explanation and every value,
 * rather than a diff against the version before. The newest is open to begin with, which on a card
 * rewritten under its reader is the version they have not read. Older versions are read on request,
 * from the top, which is where they land.
 */
const ActionHistoryDialog: React.FC<ActionHistoryDialogProps> = ({
  action,
  state,
  exposureLabel,
  versions,
  hasMore,
  isLoading,
  isError,
  onLoadOlder,
  onRetry,
  onClose,
}) => {
  // Undefined until the reader picks a row, so the newest stays the open one as older pages land.
  const [pickedVersionId, setPickedVersionId] = useState<string | null>();
  const { versions: versionRows, current } = toHistoryRows(action, state, versions, hasMore);
  const openVersionId = pickedVersionId === undefined ? versionRows.at(-1)?.versionId : pickedVersionId;

  return (
    <CardDialog isDialog fullWidth maxWidth="md" title={ACTION_HISTORY_TEXTS.DIALOG_TITLE} onClose={onClose}>
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
            {actionTitle(action)}
          </Text>
          <div className="flex flex-wrap items-center gap-8">
            <ActionStateChip state={state} action={action} />
            {exposureLabel ? (
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {exposureLabel}
              </Text>
            ) : null}
          </div>
        </div>

        {isLoading || isError ? (
          <div className="flex items-center gap-8">
            <SkeletonTable rowsCount={1} columnsCount={1} isError={isError} wrapperPadding="0" />
            {isError ? (
              <Button variant="text" className={TEXT_BUTTON_CLASSES} onClick={onRetry}>
                {ACTION_HISTORY_TEXTS.TRY_AGAIN}
              </Button>
            ) : null}
          </div>
        ) : hasMore ? (
          <Button variant="text" className={cn(TEXT_BUTTON_CLASSES, 'self-start')} onClick={onLoadOlder}>
            {ACTION_HISTORY_TEXTS.SHOW_EARLIER}
          </Button>
        ) : null}

        {versionRows.length ? (
          <ol className="m-0 list-none p-0">
            {versionRows.map((row) => {
              const isOpen = row.versionId === openVersionId;

              return (
                <TimelineRow
                  key={row.versionId}
                  node={row.isOriginal ? 'bg-slate-400' : 'bg-purple-600'}
                  time={row.time}
                  label={row.isOriginal ? ACTION_HISTORY_TEXTS.ORIGINAL : ACTION_HISTORY_TEXTS.REVISED}
                  detail={row.title}
                  // A version that recorded no payload has nothing to open.
                  onToggle={row.action ? () => setPickedVersionId(isOpen ? null : row.versionId) : undefined}
                  isOpen={isOpen}
                >
                  {row.action ? (
                    <>
                      {row.action.task_data.explanation ? (
                        <GeneralActionInner action={row.action} displayTitle={false} />
                      ) : null}
                      <PayloadList action={row.action} state={state} isSuperseded={!row.isNewest} />
                    </>
                  ) : null}
                </TimelineRow>
              );
            })}
            <TimelineRow
              isLast
              node={ACTION_STATE_APPEARANCE[state].node}
              time={current.time}
              label={current.label}
              detail={current.reason ? `“${current.reason}”` : undefined}
            />
          </ol>
        ) : null}
      </div>
    </CardDialog>
  );
};

export default ActionHistoryDialog;
